import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Shield } from 'lucide-react';
import { getBackendUrl } from '../utils/api';

const API = getBackendUrl();

const AdminLogin = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [requires2FA, setRequires2FA] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    // Always send TOTP code if provided (not empty)
    const result = await login(email, password, totpCode || null);
    
    if (result.success) {
      if (result.force_password_change) {
        // Redirect to My Account to force password change
        navigate('/admin/my-account');
      } else if (result.require_2fa_setup) {
        // Redirect to My Account to set up 2FA (enforcement is enabled)
        navigate('/admin/my-account');
      } else {
        // Fetch user info to determine role-based redirect
        try {
          const token = localStorage.getItem('adminToken');
          const response = await fetch(`${API}/admin/me`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (response.ok) {
            const userInfo = await response.json();
            // Redirect based on role
            if (userInfo.role === 'marshal') {
              navigate('/admin/check-in');
            } else {
              navigate('/admin/dashboard');
            }
          } else {
            // Fallback to dashboard if we can't get user info
            navigate('/admin/dashboard');
          }
        } catch (err) {
          console.error('Error fetching user info:', err);
          navigate('/admin/dashboard');
        }
      }
    } else {
      setError(result.error);
    }
    
    setLoading(false);
  };

  return (
    <div className="admin-login-container">
      <Card className="admin-login-card">
        <CardHeader>
          <div className="admin-login-icon">
            <Shield size={48} />
          </div>
          <CardTitle className="admin-login-title">Admin Login</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="admin-login-form">
            {error && <div className="admin-login-error">{error}</div>}
            
            <div className="form-group">
              <label htmlFor="email">Email</label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="Enter email"
                className="admin-input"
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="password">Password</label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                placeholder="Enter password"
                className="admin-input"
              />
            </div>
            
            <div style={{ textAlign: 'right', marginTop: '-8px', marginBottom: '12px' }}>
              <Link 
                to="/admin/forgot-password" 
                style={{ 
                  color: '#5a6f4e', 
                  fontSize: '0.875rem', 
                  textDecoration: 'none',
                  fontWeight: '500'
                }}
              >
                Forgot Password?
              </Link>
            </div>
            
            <div className="form-group">
              <label htmlFor="totpCode">
                2FA Code <span style={{ color: '#9ca3af', fontSize: '0.85rem' }}>(if enabled)</span>
              </label>
              <Input
                id="totpCode"
                type="text"
                value={totpCode}
                onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="000000"
                maxLength="6"
                className="admin-input"
                style={{ letterSpacing: '0.5rem', textAlign: 'center', fontSize: '1.5rem' }}
              />
              <small style={{ color: '#9ca3af', fontSize: '0.8rem', display: 'block', marginTop: '5px' }}>
                Leave blank if you haven't enabled 2FA
              </small>
            </div>
            
            <Button type="submit" disabled={loading} className="admin-login-button">
              {loading ? 'Logging in...' : 'Login'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminLogin;